import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaPhoneAlt,
  FaEnvelope,
} from 'react-icons/fa';
import logo from '../assets/images/logo.png';
import './Navbar.css';
import trainingData from '../data/trainingData'; // Import Training Data
import consultingData from '../data/consultingData'; // Import Consulting Data
import executiveTrainingData from '../data/executiveTrainingData'; // Import Executive Training Data

const Navbar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trainingDropdownOpen, setTrainingDropdownOpen] = useState(false);
  const [executiveTrainingDropdownOpen, setExecutiveTrainingDropdownOpen] = useState(false);
  const [consultingDropdownOpen, setConsultingDropdownOpen] = useState(false);
  const [calendarDropdownOpen, setCalendarDropdownOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState({}); // State for nested sub-menus
  const [navbarBackground, setNavbarBackground] = useState(
    'linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))'
  );

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (sidebarOpen) {
      setTrainingDropdownOpen(false);
      setExecutiveTrainingDropdownOpen(false);
      setConsultingDropdownOpen(false);
      setCalendarDropdownOpen(false);
      setSubMenuOpen({});
    }
  };

  // Toggle Training dropdown
  const toggleTrainingDropdown = () => {
    setTrainingDropdownOpen(!trainingDropdownOpen);
    if (!trainingDropdownOpen) {
      setExecutiveTrainingDropdownOpen(false);
      setConsultingDropdownOpen(false);
      setCalendarDropdownOpen(false);
      setSubMenuOpen({});
    }
  };

  // Toggle Executive Training dropdown
  const toggleExecutiveTrainingDropdown = () => {
    setExecutiveTrainingDropdownOpen(!executiveTrainingDropdownOpen);
    if (!executiveTrainingDropdownOpen) {
      setTrainingDropdownOpen(false);
      setConsultingDropdownOpen(false);
      setCalendarDropdownOpen(false);
      setSubMenuOpen({});
    }
  };

  // Toggle Consulting dropdown
  const toggleConsultingDropdown = () => {
    setConsultingDropdownOpen(!consultingDropdownOpen);
    if (!consultingDropdownOpen) {
      setTrainingDropdownOpen(false);
      setExecutiveTrainingDropdownOpen(false);
      setCalendarDropdownOpen(false);
      setSubMenuOpen({});
    }
  };

  // Toggle Training Calendar dropdown
  const toggleCalendarDropdown = () => {
    setCalendarDropdownOpen(!calendarDropdownOpen);
    if (!calendarDropdownOpen) {
      setTrainingDropdownOpen(false);
      setExecutiveTrainingDropdownOpen(false);
      setConsultingDropdownOpen(false);
      setSubMenuOpen({});
    }
  };

  // Toggle sub-menu for nested dropdowns
  const toggleSubMenu = (index) => {
    setSubMenuOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Helper function to close sidebar and dropdowns
  const closeSidebar = () => {
    setSidebarOpen(false);
    setTrainingDropdownOpen(false);
    setExecutiveTrainingDropdownOpen(false);
    setConsultingDropdownOpen(false);
    setCalendarDropdownOpen(false);
    setSubMenuOpen({});
  };

  // Handle navbar background on scroll
  const handleScroll = () => {
    const scrollY = window.scrollY;
    const opacity = Math.min(scrollY / 110, 1);
    setNavbarBackground(
      `linear-gradient(to bottom, rgba(255, 255, 255, ${0.1 + 0.9 * opacity}), rgba(255, 255, 255, 0))`
    );
  };

  // Add scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close sidebar when resizing to large screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && sidebarOpen) {
        setSidebarOpen(false);
        setTrainingDropdownOpen(false);
        setExecutiveTrainingDropdownOpen(false);
        setConsultingDropdownOpen(false);
        setCalendarDropdownOpen(false);
        setSubMenuOpen({});
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Menu items data
  const menuItems = [
    { title: 'Home', path: '/' },
    { title: 'About Us', path: '/about-us' },
  ];

  // Consulting dropdown items
  const consultingDropdownItems = consultingData;

  // Training dropdown items
  const trainingDropdownItems = trainingData;

  // Executive Training dropdown items
  const executiveTrainingDropdownItems = executiveTrainingData;

  // Training Calendar dropdown items
  const calendarDropdownItems = [
    { title: 'Training calendar 2025', path: '/training-calendar-2025' },
  ];

  return (
    <>
      <nav className="navbar" style={{ background: navbarBackground }}>
        <div className="navbar-content">
          {/* Logo */}
          <div className="navbar-logo">
            <Link to="/" onClick={closeSidebar}>
              <img src={logo} alt="Company Logo" />
            </Link>
          </div>

          {/* Contact Items (Visible on large screens) */}
          <div className="navbar-contact">
            <div className="contact-item">
              <FaPhoneAlt className="icon" />
              <a href="tel:+250724128627" style={{ color: '#333333', textDecoration: 'none' }}>Call Us Any Time</a>
              <p>
                <a href="tel:+250724128627" style={{ color: '#333333', textDecoration: 'none' }}>Tel: +250724128627</a> |
                <a href="tel:+254720090959" style={{ color: '#333333', textDecoration: 'none' }}>Cell: +254720090959</a>
              </p>
            </div>
            <div className="contact-item">
              <FaEnvelope className="icon" />
              <a href="mailto:info@expromconsulting.com" style={{ color: '#333333', textDecoration: 'none' }}>Email Us</a>
              <p>
                <a href="mailto:info@expromconsulting.com" style={{ color: '#333333', textDecoration: 'none' }}>info@expromconsulting.com</a>
              </p>
            </div>
          </div>

          {/* Hamburger Menu Icon (Visible on small screens) */}
          <div className="navbar-hamburger" onClick={toggleSidebar}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </div>
        </div>

        {/* Navbar List (Visible on large screens) */}
        <ul className="navbar-list">
          {menuItems.map((item, index) => (
            <li key={index}>
              <NavLink
                to={item.path}
                className={({ isActive }) => (isActive ? 'navbar-link active' : 'navbar-link')}
              >
                {item.title}
              </NavLink>
            </li>
          ))}

          {/* Training Dropdown */}
          <li className="navbar-link dropdown">
            <div className="dropdown-toggle" onClick={toggleTrainingDropdown}>
              Training <FaChevronDown className={trainingDropdownOpen ? 'rotate' : ''} />
            </div>
            <ul className={`dropdown-menu ${trainingDropdownOpen ? 'show' : ''}`}>
              {trainingDropdownItems.map((item, index) => (
                <li key={index}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                    onClick={() => setTrainingDropdownOpen(false)}
                  >
                    {item.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>

          {/* Executive Training Dropdown */}
          <li className="navbar-link dropdown">
            <div className="dropdown-toggle" onClick={toggleExecutiveTrainingDropdown}>
              Executive Training <FaChevronDown className={executiveTrainingDropdownOpen ? 'rotate' : ''} />
            </div>
            <ul className={`dropdown-menu ${executiveTrainingDropdownOpen ? 'show' : ''}`}>
              {executiveTrainingDropdownItems.map((item, index) => (
                <li key={index} className={item.subItems ? 'dropdown-submenu' : ''}>
                  {item.subItems ? (
                    <>
                      <div
                        className="dropdown-toggle"
                        onClick={() => toggleSubMenu(index)}
                      >
                        {item.title} <FaChevronDown className={subMenuOpen[index] ? 'rotate' : ''} />
                      </div>
                      <ul className={`dropdown-menu sub-menu ${subMenuOpen[index] ? 'show' : ''}`}>
                        {item.subItems.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <NavLink
                              to={subItem.path}
                              className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                              onClick={() => {
                                setExecutiveTrainingDropdownOpen(false);
                                setSubMenuOpen({});
                              }}
                            >
                              {subItem.title}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                      onClick={() => {
                        setExecutiveTrainingDropdownOpen(false);
                        setSubMenuOpen({});
                      }}
                    >
                      {item.title}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </li>

          {/* Consulting Dropdown */}
          <li className="navbar-link dropdown">
            <div className="dropdown-toggle" onClick={toggleConsultingDropdown}>
              Consulting <FaChevronDown className={consultingDropdownOpen ? 'rotate' : ''} />
            </div>
            <ul className={`dropdown-menu ${consultingDropdownOpen ? 'show' : ''}`}>
              {consultingDropdownItems.map((item, index) => (
                <li key={index}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                    onClick={() => setConsultingDropdownOpen(false)}
                  >
                    {item.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>

          {/* Training Calendar Dropdown */}
          <li className="navbar-link dropdown">
            <div className="dropdown-toggle" onClick={toggleCalendarDropdown}>
              Training Calendar <FaChevronDown className={calendarDropdownOpen ? 'rotate' : ''} />
            </div>
            <ul className={`dropdown-menu ${calendarDropdownOpen ? 'show' : ''}`}>
              {calendarDropdownItems.map((item, index) => (
                <li key={index}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                    onClick={() => setCalendarDropdownOpen(false)}
                  >
                    {item.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        </ul>

        {/* Sidebar (Visible on small screens when sidebarOpen is true) */}
        <div className={`sidebar ${sidebarOpen ? 'active' : ''}`}>
          <ul className="sidebar-list">
            {menuItems.map((item, index) => (
              <li key={index}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                  onClick={toggleSidebar}
                >
                  {item.title}
                </NavLink>
              </li>
            ))}

            {/* Training Link in Sidebar */}
            <li>
              <NavLink
                to="/training"
                className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                onClick={toggleSidebar}
              >
                Training
              </NavLink>
            </li>

            {/* Executive Training Link in Sidebar */}
            <li>
              <NavLink
                to="/executive-training"
                className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                onClick={toggleSidebar}
              >
                Executive Training
              </NavLink>
            </li>

            {/* Consulting Link in Sidebar */}
            <li>
              <NavLink
                to="/consulting"
                className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                onClick={toggleSidebar}
              >
                Consulting
              </NavLink>
            </li>

            {/* Training Calendar Dropdown in Sidebar */}
            <li className="sidebar-link dropdown">
              <div className="dropdown-toggle" onClick={toggleCalendarDropdown}>
                Training Calendar <FaChevronDown className={calendarDropdownOpen ? 'rotate' : ''} />
              </div>
              <ul className={`dropdown-menu ${calendarDropdownOpen ? 'show' : ''}`}>
                {calendarDropdownItems.map((item, index) => (
                  <li key={index}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                      onClick={() => {
                        toggleSidebar();
                        setCalendarDropdownOpen(false);
                      }}
                    >
                      {item.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>

        {/* Overlay to close sidebar when clicking outside */}
        {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
      </nav>
    </>
  );
};

export default Navbar;